import { Form, Formik, FormikProps } from 'formik';
import { useRef, useState } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import management from '../../assets/Consumption-management.svg';
import BusinessInfo from '../../components/BusinessInfo/BusinessInfo';
import Input from '../../components/Input/Input';
import NavigationFooter from '../../components/NavigationFooter/NavigationFooter';
import Spinner from '../../components/Spinner/Spinner';
import Status from '../../components/Status/Status';
import { getCommerce, getDgiBusinessInfo, updateBusinessExecutive } from '../../services/commerce.service';
import {
  AddCommerceSixthAndFinalStepLocationState,
  Contract,
  EcommerceTariffValues,
} from '../../types/add_commerce.types';
import { FormStatusEnum } from '../../types/enums/form-status.enum';
import { FormStepEnum } from '../../types/enums/form-step.enum';
import { isZurichChannelAllowed } from '../../utils/institutionUtils';
import { getAddBusinessPage, pagesPaths } from '../../utils/navigationUtils';
import schemas from '../../utils/schemas';
import { CONTRACT_SIGNATURE_PENDING_STATUS, getStatusValue } from '../../utils/statusHelpers';

interface SearchCommerceValues {
  rut: number | string;
}

export interface CommerceStatus {
  state: FormStatusEnum;
  step: FormStepEnum;
}

// The backend guarantees `businessId` on a business record; `id` is only populated at some
// wizard steps. Centralizing this here means every downstream consumer of a RUT search result
// (commerceId state, BusinessInfo, and whatever it triggers) reads the same validated value
// instead of each one separately guessing at `id` vs `businessId`.
function getValidBusinessId(businessData: any): number | null {
  const rawId = businessData?.id ?? businessData?.businessId;
  if (rawId === undefined || rawId === null || rawId === '') return null;
  const numericId = Number(rawId);
  return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
}

function SearchCommerce() {
  const [commerceExists, setCommerceExists] = useState<boolean | null>(null);
  const [commerceMessage, setCommerceMessage] = useState<string>('');
  const [commerceRut, setCommerceRut] = useState<number>(0);
  const [businessData, setBusinessData] = useState<AddCommerceSixthAndFinalStepLocationState | any>({});
  const [commerceStatus, setCommerceStatus] = useState<CommerceStatus | null>(null);
  const [commerceId, setCommerceId] = useState<number>(0);
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const ecommerceAndTariffsForm = useRef<FormikProps<EcommerceTariffValues>>(null);
  const [uploadedContract, setUploadedContract] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  const submitSearchCommerce = async (values: SearchCommerceValues) => {
    setShowSpinner(true);
    const getCommerceResponse = await getCommerce(Number(values.rut));
    setShowSpinner(false);

    if (getCommerceResponse.status !== 401) {
      setCommerceRut(Number(values.rut));

      if (getCommerceResponse.data) {
        setCommerceMessage(getCommerceResponse.data.message);
        setCommerceExists(getCommerceResponse.data.rutExists);
        setCommerceStatus({ state: getCommerceResponse.data.state, step: getCommerceResponse.data.step });

        const rawBusinessData = getCommerceResponse.data.businessData;
        setBusinessData(rawBusinessData || {});
        setCommerceId(getValidBusinessId(rawBusinessData) ?? 0);
      }
    } else {
      localStorage.removeItem('fullName');
    }
  };

  const handleSaveData = async () =>
    ecommerceAndTariffsForm.current && ecommerceAndTariffsForm.current.handleSubmit();

  const handleMoreInfo = async () => {
    const path = getAddBusinessPage(commerceStatus?.step!);

    if (commerceStatus?.state === FormStatusEnum.FREE) {
      await updateBusinessExecutive(+commerceId);
    }

    if (path) {
      setCommerceStatus(null);

      const getDgiInfo = path === pagesPaths.CommerceInformation && !Object.keys(businessData).length;
      if (getDgiInfo) {
        setShowSpinner(true);
        const dgiInfo = await getDgiBusinessInfo(+commerceRut);
        setShowSpinner(false);

        if (dgiInfo.status >= 400) {
          return navigate(path, { state: { commerceRut, from: pagesPaths.SearchCommerce } });
        } else {
          return navigate(path, {
            state: { commerceRut, ...businessData, ...dgiInfo.data, from: pagesPaths.SearchCommerce },
          });
        }
      }

      const isFinished = businessData.status === FormStatusEnum[FormStatusEnum.FINISHED];
      const contractsAllSigned: boolean = businessData?.contracts?.every(
        (c: Contract) => c.vaFirmaStatus === 'SIGNED' || c.vaFirmaStatus === 'NOT_SENDED',
      );

      // When the onboarding is finished and all contracts are signed, always land on
      // CommerceSummary regardless of the step stored in the backend (step can still be 9
      // even for a fully signed commerce). hideEmailSection hides the "send contracts" form
      // in CommerceSummary for all finished onboardings; the contracts screen itself already
      // shows the correct read-only view when contractsSigned is true.
      const targetPath = isFinished && contractsAllSigned ? pagesPaths.CommerceSummary : path;
      const hideEmailSection: boolean = targetPath === pagesPaths.CommerceSummary && isFinished;

      // Resuming can jump straight past CommerceTerminals (whose own submit-time guard never
      // runs), so a Zurich insurance persisted under a previously eligible channel/role must be
      // stripped here too before it reaches any later step.
      const sanitizedBusinessData =
        !isZurichChannelAllowed() && businessData?.insurances?.length > 0
          ? { ...businessData, insurances: [] }
          : businessData;

      navigate(targetPath, {
        state: { commerceRut, ...sanitizedBusinessData, from: pagesPaths.SearchCommerce, hideEmailSection },
      });
    }
  };

  const showFooter =
    (commerceExists && commerceStatus?.state === FormStatusEnum.IN_PROCESS) ||
    (commerceExists && commerceStatus?.state === FormStatusEnum.FINISHED) ||
    (!commerceExists && commerceStatus?.state === FormStatusEnum.EMPTY) ||
    (commerceExists && commerceStatus?.state === FormStatusEnum.FREE);

  const generateStatus = (businessData: AddCommerceSixthAndFinalStepLocationState) => {
    if (uploadedContract) {
      return FormStatusEnum[FormStatusEnum.FINISHED];
    } else {
      if (businessData.status === FormStatusEnum[FormStatusEnum.FINISHED]) {
        const contractsAreSigned = businessData?.contracts?.every(
          (contract: Contract) =>
            contract.vaFirmaStatus === 'SIGNED' || contract.vaFirmaStatus === 'NOT_SENDED',
        );

        return contractsAreSigned
          ? FormStatusEnum[FormStatusEnum.FINISHED]
          : CONTRACT_SIGNATURE_PENDING_STATUS;
      }

      return businessData.status;
    }
  };

  // The ESTADO badge (below) buckets any unrecognized granular status under "Sin completar" —
  // reuse that exact check so the info panel shows up whenever the badge says "Sin completar",
  // regardless of the wizard's coarser step/state values.
  const isIncompleteStatus = getStatusValue(generateStatus(businessData)) === 'Sin completar';

  const showBusinessInfo =
    !!Object.keys(businessData).length &&
    getValidBusinessId(businessData) !== null &&
    (commerceStatus?.step === FormStepEnum.SECOND ||
      commerceStatus?.step === FormStepEnum.TENTH ||
      isIncompleteStatus);

  const contractsSigned: boolean = businessData?.contracts?.every(
    (contract: Contract) => contract.vaFirmaStatus === 'SIGNED' || contract.vaFirmaStatus === 'NOT_SENDED',
  );
  const showSaveDataButton: boolean =
    contractsSigned && !businessData?.hasEcommerce && businessData?.principalEmail;

  const handleStatus = () => {
    setUploadedContract(!uploadedContract);
  };

  // Passed down to BusinessInfo/ShareBlock so that deleting the onboarding from there resets
  // this screen back to its initial "search again" state.
  const handleOnboardingDeleted = () => {
    setCommerceExists(null);
    setCommerceMessage('');
    setBusinessData({});
    setCommerceStatus(null);
    setCommerceRut(0);
    setCommerceId(0);
    setUploadedContract(false);
  };

  return (
    <>
      <Container className="search-commerce">
        <Row>
          <Col sm={12}>
            <h1 className="mb-5">
              <img src={management} alt="Comercio" className="me-3" />
              Búsqueda de comercio
            </h1>
            <Formik
              initialValues={{ rut: '' }}
              validationSchema={schemas.SearchCommerceSchema}
              onSubmit={submitSearchCommerce}
            >
              <Form className="row align-items-end">
                <Input
                  type="tel"
                  name="rut"
                  placeholder="RUT"
                  className="flex-grow-1 col-9 col-md-11 pb-3"
                  label="Ingrese número de RUT"
                />
                <Col sm={12} md={1}>
                  <Button
                    type="submit"
                    variant="outline-danger"
                    className="primary-outlined-button search-button"
                  >
                    Buscar
                  </Button>
                </Col>
              </Form>
            </Formik>
            <Spinner visible={showSpinner} />
            {commerceMessage && !showSpinner && <p className="search-description">{commerceMessage}</p>}
            {businessData.status && !showSpinner && <Status status={generateStatus(businessData)} />}
            {showBusinessInfo && !showSpinner && (
              <div className="confirmation col-sm-12 col-md-11">
                <BusinessInfo
                  businessData={businessData as AddCommerceSixthAndFinalStepLocationState}
                  showAttachments
                  checkboxState={businessData.surchargedFee}
                  anticipatePayments={businessData.anticipatePayments}
                  comment={businessData.comment}
                  status={commerceStatus?.state!}
                  ecommerceAndTariffsForm={ecommerceAndTariffsForm}
                  commerceId={+commerceId}
                  comesFromSearchCommerce
                  handleStatus={handleStatus}
                  uploadedContract={uploadedContract}
                  setIsLoading={setIsLoading}
                  onOnboardingDeleted={handleOnboardingDeleted}
                />
              </div>
            )}
          </Col>
        </Row>
      </Container>
      {showFooter && (
        <NavigationFooter
          nextHandler={handleMoreInfo}
          nextText={!commerceExists ? 'Dar de alta' : showBusinessInfo ? 'Más información' : 'Continuar'}
          previousText={showSaveDataButton ? 'Guardar información' : false}
          previousHandler={showSaveDataButton ? handleSaveData : false}
          showPreviousIcon={showSaveDataButton ? false : undefined}
          loading={isLoading}
        />
      )}
    </>
  );
}

export default SearchCommerce;
